import { IconifyIcon } from '@iconify/react';
import React from 'react';
import { twMerge } from 'tailwind-merge';

import Button, { ButtonVariant } from '@components/Button';
import MemoizedIcon from '@components/MemoizedIcon';

export interface PlaceholderViewProps {
  action?: () => void;
  subAction?: () => void;
  icon: IconifyIcon;
  iconContainerClassName?: string;
  iconClassName?: string;
  mainText: string;
  mainTextClassName?: string;
  subText?: string;
  subTextClassName?: string;
  actionText?: string;
  subActionText?: string;
  actionTextClassName?: string;
  subActionTextClassName?: string;
  containerClassName?: string;
  dataTestId?: string;
}

export const PlaceholderView = ({
  action,
  subAction,
  icon,
  iconContainerClassName,
  iconClassName,
  mainText,
  mainTextClassName,
  subText,
  subTextClassName,
  actionText,
  subActionText,
  actionTextClassName,
  subActionTextClassName,
  containerClassName,
  dataTestId = 'placeholder-view',
}: PlaceholderViewProps) => (
  <div
    data-testid={dataTestId}
    className={twMerge(
      'flex flex-col h-fit justify-center items-center flex-none',
      containerClassName,
    )}
  >
    <div
      data-testid={`${dataTestId}-icon-container`}
      className={twMerge(
        'flex flex-none justify-center items-center rounded-full h-20 w-20 sm:h-[100px] sm:w-[100px] mb-4 sm:mb-7 bg-primary-50 text-primary-500',
        iconContainerClassName,
      )}
    >
      <MemoizedIcon
        data-testid={`${dataTestId}-icon`}
        icon={icon}
        className={twMerge(
          'h-[48px] sm:h-[55px] w-[48px] sm:w-[55px]',
          iconClassName,
        )}
      />
    </div>
    <p
      data-testid={`${dataTestId}-main`}
      className={twMerge(
        'font-semibold text-base sm:text-2xl mb-2 sm:mb-4 text-center',
        mainTextClassName,
      )}
    >
      {mainText}
    </p>
    {subText && (
      <p
        data-testid={`${dataTestId}-sub`}
        className={twMerge(
          'capitalize text-neutral-80 text-sm sm:text-lg mb-4 sm:mb-11 text-center whitespace-pre-line',
          subTextClassName,
        )}
      >
        {subText}
      </p>
    )}
    <div className="flex gap-4">
      {action && (
        <Button
          dataTestId={`${dataTestId}-action`}
          onClick={action}
          className={twMerge('px-[55px] sm:px-[55px]', actionTextClassName)}
        >
          {actionText}
        </Button>
      )}
      {subAction && (
        <Button
          dataTestId={`${dataTestId}-sub-action`}
          onClick={subAction}
          className={twMerge('px-[55px] sm:px-[55px]', subActionTextClassName)}
          variant={ButtonVariant.Secondary}
        >
          {subActionText}
        </Button>
      )}
    </div>
  </div>
);
